import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

interface UseGoogleOneTapOptions {
  enabled?: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const useGoogleOneTap = (options: UseGoogleOneTapOptions = {}) => {
  const { enabled = true, onSuccess, onError } = options;
  const { loginWithGoogle, isAuthenticated } = useAuth();
  const hasInitialized = useRef(false);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!enabled || !clientId || isAuthenticated || hasInitialized.current) {
      return;
    }

    const initializeOneTap = () => {
      if (window.google?.accounts?.id) {
        hasInitialized.current = true;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            if (response.credential) {
              try {
                await loginWithGoogle(response.credential);
                onSuccess?.();
              } catch (err) {
                console.error('One Tap sign-in failed:', err);
                onError?.('One Tap sign-in failed');
              }
            }
          },
          auto_select: true,
          cancel_on_tap_outside: false,
        });

        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('One Tap not displayed:', notification.getNotDisplayedReason());
          } else if (notification.isSkippedMoment()) {
            console.log('One Tap skipped:', notification.getSkippedReason());
          }
        });
      }
    };

    if (window.google?.accounts?.id) {
      initializeOneTap();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeOneTap;
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [enabled, clientId, isAuthenticated, loginWithGoogle, onSuccess, onError]);
};
